import React, { useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import type { Package } from '../../hooks/usePackages';
import { usePackages } from '../../hooks/usePackages';

interface SellPackageModalProps {
  pkg: Package;
  onClose: () => void;
  onSold?: () => void;
}

export default function SellPackageModal({ pkg, onClose, onSold }: SellPackageModalProps) {
  const { sellPackage, getAvailableQty } = usePackages();
  const [qtySold, setQtySold] = useState(1);
  const [selling, setSelling] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const unlimitedItems = (pkg.items ?? []).filter(
    (i) => i.product?.inventory_type === 'unlimited'
  );
  const [costOverrides, setCostOverrides] = useState<Record<string, number>>(() => {
    const defaults: Record<string, number> = {};
    unlimitedItems.forEach((item) => {
      if (item.unit_cost_override != null) {
        defaults[item.product_id] = item.unit_cost_override;
      }
    });
    return defaults;
  });

  const _availQty = getAvailableQty(pkg);
  const trackedItems = (pkg.items ?? []).filter((i) => i.product?.inventory_type === 'tracked');
  const lowStockItems = trackedItems.filter(
    (i) => (i.product?.stock_quantity ?? 0) < i.quantity * qtySold
  );

  const totalUnlimitedCost = unlimitedItems.reduce((sum, item) => {
    const unitCost = costOverrides[item.product_id] ?? item.unit_cost_override ?? 0;
    return sum + unitCost * item.quantity * qtySold;
  }, 0);

  const basePrice = pkg.price ?? 0;
  const totalPrice = basePrice * qtySold;

  const handleSell = async () => {
    setErrorMsg(null);
    setSelling(true);
    const result = await sellPackage(pkg.id, qtySold, costOverrides);
    setSelling(false);
    if (result.success) {
      if (result.warnings.length > 0) {
        setErrorMsg(result.warnings.join('. '));
      }
      onSold?.();
      onClose();
    } else {
      setErrorMsg('No se pudo procesar la venta.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="font-semibold text-gray-900">{pkg.name}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Registrar venta de paquete</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Cantidad a vender
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setQtySold((q) => Math.max(1, q - 1))}
                className="w-9 h-9 border border-gray-200 rounded-xl flex items-center justify-center text-lg font-medium text-gray-600 hover:bg-gray-50"
              >
                −
              </button>
              <input
                type="number"
                value={qtySold}
                onChange={(e) => setQtySold(Math.max(1, parseInt(e.target.value, 10) || 1))}
                min={1}
                className="w-20 text-center text-lg font-semibold border border-gray-200 rounded-xl py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <button
                type="button"
                onClick={() => setQtySold((q) => q + 1)}
                className="w-9 h-9 border border-gray-200 rounded-xl flex items-center justify-center text-lg font-medium text-gray-600 hover:bg-gray-50"
              >
                +
              </button>
            </div>
          </div>

          {lowStockItems.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
                Stock insuficiente — venta permitida de todos modos
              </div>
              {lowStockItems.map((item) => (
                <p key={item.product_id} className="text-xs text-amber-600 pl-7">
                  {item.product?.name}: necesitas {item.quantity * qtySold}, disponibles{' '}
                  {item.product?.stock_quantity ?? 0}
                </p>
              ))}
            </div>
          )}

          {unlimitedItems.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                <span className="text-blue-500">∞</span> Servicios incluidos
              </p>
              <div className="space-y-2">
                {unlimitedItems.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex items-center justify-between gap-3 bg-blue-50 rounded-xl px-3 py-2"
                  >
                    <span className="text-sm text-gray-700">
                      {item.quantity}x {item.product?.name}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">$</span>
                      <input
                        type="number"
                        value={costOverrides[item.product_id] ?? ''}
                        onChange={(e) =>
                          setCostOverrides((prev) => ({
                            ...prev,
                            [item.product_id]: parseFloat(e.target.value) || 0,
                          }))
                        }
                        placeholder="0.00"
                        min={0}
                        step={0.01}
                        className="w-20 text-right text-sm border border-blue-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <span className="text-xs text-gray-400">/u</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            {pkg.price != null && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Precio paquete × {qtySold}</span>
                <span>
                  ${totalPrice.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            {totalUnlimitedCost > 0 && (
              <div className="flex justify-between text-sm text-blue-600">
                <span>Servicios adicionales</span>
                <span>
                  ${totalUnlimitedCost.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            {pkg.price != null && (
              <div className="flex justify-between text-base font-semibold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span>
                  ${(totalPrice + totalUnlimitedCost).toLocaleString('es-CO', {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-gray-400 mb-2 font-medium">SE DESCONTARÁ DEL INVENTARIO</p>
            <div className="space-y-1">
              {trackedItems.map((item) => (
                <div
                  key={item.product_id}
                  className="flex justify-between text-xs text-gray-600"
                >
                  <span>{item.product?.name}</span>
                  <span>−{item.quantity * qtySold} unidades</span>
                </div>
              ))}
              {trackedItems.length === 0 && (
                <p className="text-xs text-gray-400 italic">
                  Sin ítems de inventario en este paquete
                </p>
              )}
            </div>
          </div>
        </div>

        {errorMsg && (
          <div className="mx-5 mb-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {errorMsg}
          </div>
        )}

        <div className="flex gap-3 p-5 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSell}
            disabled={selling}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50"
          >
            <ShoppingCartIcon className="w-4 h-4" />
            {selling ? 'Procesando...' : 'Confirmar venta'}
          </button>
        </div>
      </div>
    </div>
  );
}
